import React, { useState, useEffect } from 'react';
import shopify from "../../../../../js/shopify";
import hasher from "../../../../../js/alpine/stores/hasher";

declare global {
  interface Window {
    customerId?: string;
  }
}

interface Store {
  name: string;
  address_street_1?: string;
  address_street_2?: string;
  city?: string;
  state?: string;
  postcode?: string | number;
  country?: string;
  phone_number?: string;
}

const AccountPreferredStore = () => {
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [lastSavedStore, setLastSavedStore] = useState<Store | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const handleSave = async () => {
    if (!selectedStore) return;
  
    try {
      const customerId = window.customerId;
  
      if (!customerId) {
        setErrorMessage("Customer ID not available.");
        setSuccessMessage("");
        return;
      }
  
      const query = `
        mutation customerUpdate($input: CustomerInput!) {
          customerUpdate(input: $input) {
            customer {
              id
            }
            userErrors {
              field
              message
            }
          }
        }
      `;
  
      const variables = {
        input: {
          id: `gid://shopify/Customer/${customerId}`,
          metafields: [
            {
              namespace: "custom",
              key: "store_name",
              type: "single_line_text_field",
              value: selectedStore.name || "",
            },
            {
              namespace: "custom",
              key: "address_street_1",
              type: "single_line_text_field",
              value: selectedStore.address_street_1 || "",
            },
            {
              namespace: "custom",
              key: "address_street_2",
              type: "single_line_text_field",
              value: selectedStore.address_street_2 || "",
            },
            {
              namespace: "custom",
              key: "city",
              type: "single_line_text_field",
              value: selectedStore.city || "",
            },
            {
              namespace: "custom",
              key: "state",
              type: "single_line_text_field",
              value: selectedStore.state || "",
            },
            {
              namespace: "custom",
              key: "postcode",
              type: "single_line_text_field",
              value: selectedStore.postcode?.toString() || "",
            },
            {
              namespace: "custom",
              key: "country",
              type: "single_line_text_field",
              value: selectedStore.country || "",
            },
            {
              namespace: "custom",
              key: "phone_number",
              type: "single_line_text_field",
              value: selectedStore.phone_number || "",
            }
          ]
        }
      };
  
      const body = JSON.stringify({
        query,
        variables,
      });
  
      const fetchDetails = {
        options: {
          method: 'POST',
          headers: {
            'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
            'Content-Type': 'application/json',
            'X-MindArc-Hash': hasher.store().getHash(),
          },
          body: body,
          request_body: query,
        },
      };
  
      const response = await shopify.authenticatedFetch(fetchDetails);
      setSuccessMessage("Update successful!");
      setErrorMessage("");
      setLastSavedStore(selectedStore);
      localStorage.setItem("preferredStoreName", selectedStore.name);
    } catch (error) {
      setErrorMessage("Error updating information");
      setSuccessMessage("");
    }
  };

  useEffect(() => {
    const handlePreferredStoreUpdate = (e) => {
      setSelectedStore(e.detail);
    };

    window.addEventListener("preferredStoreUpdated", handlePreferredStoreUpdate);

    const fetchPreferredStore = async () => {
      try {
        const customerId = window.customerId;
        if (!customerId) return;

        const query = `
          query {
            customer(id: "gid://shopify/Customer/${customerId}") {
              metafields(first: 10, namespace: "custom") {
                edges {
                  node {
                    key
                    value
                  }
                }
              }
            }
          }
        `;

        const fetchDetails = {
          options: {
            method: 'POST',
            headers: {
              'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
              'Content-Type': 'application/json',
              'X-MindArc-Hash': hasher.store().getHash(),
            },
            body: JSON.stringify({ query }),
            request_body: query,
          },
        };

        const response = await shopify.authenticatedFetch(fetchDetails);
        const metafields = response?.data?.customer?.metafields?.edges || [];

        const storeData: Record<string, string> = {};
        metafields.forEach(({ node }) => {
          storeData[node.key] = node.value;
        });

        if (Object.keys(storeData).length > 0) {
          const store = {
            name: storeData.store_name || '',
            address_street_1: storeData.address_street_1 || '',
            address_street_2: storeData.address_street_2 || '',
            city: storeData.city || '',
            state: storeData.state || '',
            postcode: storeData.postcode || '',
            country: storeData.country || '',
            phone_number: storeData.phone_number || '',
          };
          setSelectedStore(store);
          setLastSavedStore(store);
        }
      } catch (err) {
        console.error("Error fetching preferred store metafields", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferredStore();

    return () => {
      window.removeEventListener("preferredStoreUpdated", handlePreferredStoreUpdate);
    };
  }, []);

  return (
    <>
      <section className="mt-8">
        <label className="text-[#54565A] b2 block mb-4">
          My preferred store
        </label>

        <div className="border border-[#A2A9AD] py-6 px-4 flex flex-col gap-4 justify-between lg:p-6 lg:flex-row lg:gap-0">
          <div>
            <span className="bc text-[#54565A] uppercase mb-4 block">
              Selected store
            </span>
            {isLoading ? (
              <div className='text-[#54565A] blabel'>Loading preferred store...</div>
            ) : selectedStore ? (
              <div className='flex flex-col space-y-[2px] text-black b2'>
                <p>{selectedStore.name}</p>
                <p>{selectedStore.address_street_1}</p>
                <p>{selectedStore.address_street_2}</p>
                <p>{selectedStore.city} {selectedStore.state}, {selectedStore.postcode}</p>
                <p>{selectedStore.country}</p>
                <p>{selectedStore.phone_number}</p>
              </div>
            ) : (
              <div className='text-[#54565A] b2'>No store selected.</div>
            )}
          </div>

          <button
            className="!px-5 !py-4 button btn-outline w-max"
            onClick={() => {
              const event = new CustomEvent("openStoreModalFromAccount");
              window.dispatchEvent(event);
            }}
          >
            <i className='hidden text-2xl text-black icon icon-location-wide lg:block'></i> Change store
          </button>
        </div>

        <button
          className="button btn-light w-full mt-10 mb-[26px] lg:my-10 lg:max-w-[280px]"
          disabled={
            !selectedStore ||
            JSON.stringify(selectedStore) === JSON.stringify(lastSavedStore)
          }
          onClick={handleSave}
        >
          Save Changes
        </button>
      </section>

      {successMessage && <div className="!mt-0 form-success">{successMessage}</div>}
      {errorMessage && <div className="!mt-0 errors">{errorMessage}</div>}
    </>
  );
};

export default AccountPreferredStore;